const DEFAULT_IMAGES_BASE = "https://images.mixitone.com";

function readViteImagesBase(): string | undefined {
  try {
    const env = import.meta.env?.VITE_MIXITONE_IMAGES_URL;
    if (typeof env === "string" && env.trim() !== "") {
      return env.trim().replace(/\/+$/, "");
    }
  } catch {
    // import.meta.env unavailable (non-Vite)
  }
  return undefined;
}

/** Public HTTPS base for the R2-backed images bucket (no trailing slash). */
export function mixitoneImagesBaseUrl(): string {
  return readViteImagesBase() ?? DEFAULT_IMAGES_BASE;
}

/** Full public URL for an object key such as `{accountId}/{uuid}` or `{accountId}/documents/{uuid}`. */
export function mixitoneImagesPublicUrl(objectKey: string): string {
  const base = mixitoneImagesBaseUrl();
  const key = objectKey.replace(/^\/+/, "");
  return `${base}/${key}`;
}

/** Options for {@link mixitoneImagesTransformedUrl} (Cloudflare `/cdn-cgi/image/`). */
export type MixitoneImagesTransformOptions = {
  width?: number;
  height?: number;
  fit?: "scale-down" | "contain" | "cover" | "crop" | "pad" | "squeeze";
  /** e.g. `auto` for WebP/AVIF where supported */
  format?: "auto" | "webp" | "avif" | "jpeg" | "png";
  quality?: number;
};

/**
 * URL with [Cloudflare Image Transformations](https://developers.cloudflare.com/images/transform-images/transform-via-url/)
 * on the images zone. Use for raster previews only, not PDFs.
 */
export function mixitoneImagesTransformedUrl(
  objectKey: string,
  options: MixitoneImagesTransformOptions = {},
): string {
  const width = options.width ?? 800;
  const fit = options.fit ?? "scale-down";
  const segments = [`width=${width}`, `fit=${fit}`];
  if (options.height != null) segments.push(`height=${options.height}`);
  if (options.format != null) segments.push(`format=${options.format}`);
  if (options.quality != null) segments.push(`quality=${options.quality}`);
  const opts = segments.join(",");
  const base = mixitoneImagesBaseUrl().replace(/\/+$/, "");
  const key = objectKey.replace(/^\/+/, "");
  return `${base}/cdn-cgi/image/${opts}/${key}`;
}
